// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import ProtectedContent from "@werner/App/Components/ProtectedContent/ProtectedContent";
import XngAppProvider from "@werner/App/Components/XngAppProvider/XngAppProvider";
import { CustomAppState } from "@werner/App/Store/Reducers";
import * as React from "react";
import { ReactNode } from "react";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

const pageStyle: MobiusPageProps = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        &&&& {
            padding-bottom: 15px;
            & ~ main {
                padding-top: 0;
                position: relative;
            }
        }
        ${({ theme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        overflow: initial;
                    `,
                    css`
                        overflow: initial;
                        margin: -5px;
                        > * {
                            padding: 5px;
                        }
                    `,
                ],
                "max",
            )}

        @media print {
            padding: 10px;
        }
    `,
};

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: PageLayoutProps) => {
    // <XNG-Changes>
    const { breadcrumbActions } = useAppSelector(state => ({
        breadcrumbActions: (state as CustomAppState).xCommon.breadcrumbActions,
    }));
    // </XNG-Changes>
    return (
        <PageLayoutWrapper>
            <XngAppProvider>
                {showHeader && header}
                {showCompactHeader && compactHeader}
                {showBreadcrumbs && (
                    <Page as="div" {...pageStyle}>
                        <PageBreadcrumbs />
                        {breadcrumbActions}
                    </Page>
                )}
                <ProtectedContent>{pageContent}</ProtectedContent>
                {showFooter && footer}
                {showSignInOverlay && signInOverlay}
                {showStorePickupOverlay && storePickupOverlay}
                {showAddressBookOverlay && addressBookOverlay}
            </XngAppProvider>
            <CookiePrivacyPolicyPopup />
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default PageLayout;
